import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { Megaphone, Plus, Send, CheckCircle2, Upload, Archive, Trash2, History, Clock, Mail, ExternalLink, MousePointerClick, Eye } from 'lucide-react';
import EmptyState from '../components/common/EmptyState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  evidence_attached: 'bg-indigo-100 text-indigo-700',
  awaiting_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  scheduled: 'bg-cyan-100 text-cyan-700',
  published: 'bg-blue-100 text-blue-700',
  archived: 'bg-gray-200 text-gray-500',
};

const DELIVERY_STATE_COLORS = {
  provider_accepted: 'bg-sky-100 text-sky-700',
  processed: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  deferred: 'bg-amber-100 text-amber-700',
  bounced: 'bg-red-100 text-red-700',
  blocked: 'bg-red-100 text-red-700',
  dropped: 'bg-red-100 text-red-700',
  spam_reported: 'bg-rose-100 text-rose-700',
};

const CommunicationsCenterPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === 'super_admin';
  const [tab, setTab] = useState('communications');
  const [taxonomy, setTaxonomy] = useState({ comm_types: [], statuses: [], timeline_categories: [] });
  const [items, setItems] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryStats, setDeliveryStats] = useState({ total: 0, by_state: {} });
  const [deliveriesLoaded, setDeliveriesLoaded] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ title: '', body: '', comm_type: 'company_announcement', featured: false });
  const [tlForm, setTlForm] = useState({ title: '', description: '', event_date: '', category: 'milestone' });

  const token = localStorage.getItem('token');
  const H = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    const [t, l, tl] = await Promise.all([
      fetch(`${API_URL}/api/communications/taxonomy`, { headers: H }).then((r) => r.json()).catch(() => ({})),
      fetch(`${API_URL}/api/communications`, { headers: H }).then((r) => r.json()).catch(() => ({ communications: [] })),
      fetch(`${API_URL}/api/communications/timeline`, { headers: H }).then((r) => r.json()).catch(() => ({ timeline: [] })),
    ]);
    if (t.comm_types) setTaxonomy(t);
    setItems(l.communications || []);
    setTimeline(tl.timeline || []);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const loadDeliveries = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/communications/deliveries`, { headers: H }).then((x) => x.json());
      setDeliveries(r.deliveries || []);
      setDeliveryStats(r.stats || { total: 0, by_state: {} });
    } catch { /* non-blocking */ }
    finally { setDeliveriesLoaded(true); }
  }, []); // eslint-disable-line

  useEffect(() => { if (tab === 'deliveries') loadDeliveries(); }, [tab, loadDeliveries]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const createComm = async () => {
    if (form.title.trim().length < 3) return flash('Title too short');
    const r = await fetch(`${API_URL}/api/communications`, { method: 'POST', headers: H, body: JSON.stringify(form) });
    if (r.ok) { setForm({ title: '', body: '', comm_type: 'company_announcement', featured: false }); flash('Draft created'); load(); }
    else flash('Create failed');
  };

  const act = async (id, action) => {
    const r = await fetch(`${API_URL}/api/communications/${id}/action/${action}`, { method: 'POST', headers: H });
    if (r.ok) { flash(`Action '${action}' applied`); load(); }
    else { const d = await r.json().catch(() => ({})); flash(d.detail || `Cannot ${action}`); }
  };

  const attachEvidence = async (id) => {
    const source = window.prompt('Evidence source (e.g., decision_ledger, support, compliance):');
    if (!source) return;
    const note = window.prompt('Evidence note (optional):') || '';
    const r = await fetch(`${API_URL}/api/communications/${id}/evidence`, { method: 'POST', headers: H, body: JSON.stringify({ source, reference: '', note }) });
    if (r.ok) { flash('Evidence attached'); load(); } else flash('Failed');
  };

  const delComm = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    const r = await fetch(`${API_URL}/api/communications/${id}`, { method: 'DELETE', headers: H });
    if (r.ok) { flash('Deleted'); load(); } else { const d = await r.json().catch(() => ({})); flash(d.detail || 'Delete failed'); }
  };

  const addTimeline = async () => {
    if (tlForm.title.trim().length < 2 || !tlForm.event_date) return flash('Title & date required');
    const r = await fetch(`${API_URL}/api/communications/timeline/entry`, { method: 'POST', headers: H, body: JSON.stringify(tlForm) });
    if (r.ok) { setTlForm({ title: '', description: '', event_date: '', category: 'milestone' }); flash('Timeline entry added'); load(); }
    else flash('Add failed (owner only)');
  };

  const delTimeline = async (id) => {
    if (!window.confirm('Delete timeline entry?')) return;
    const r = await fetch(`${API_URL}/api/communications/timeline/entry/${id}`, { method: 'DELETE', headers: H });
    if (r.ok) { flash('Deleted'); load(); } else flash('Delete failed');
  };

  const fmt = (s) => (s || '').replace(/_/g, ' ');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="communications-center">
        <div className="flex items-center gap-3">
          <Megaphone className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Communications Center</h1>
            <p className="text-sm text-gray-500">Governed company communications & timeline. Owner approval required to publish.</p>
          </div>
        </div>

        {msg && <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-2" data-testid="comms-flash">{msg}</div>}
        {!isOwner && <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2">You can create drafts, attach evidence, and submit for review. Publishing (Approve / Publish / Archive) and the Company Timeline require the <strong>super_admin</strong> application role, which carries the Founder's governance authority to release communications.</div>}

        <div className="flex gap-2 flex-wrap">
          {['communications', 'deliveries', 'timeline'].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`comms-tab-${t}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {t === 'communications' ? 'Communications' : t === 'deliveries' ? 'Operational Deliveries' : 'Company Timeline'}
            </button>
          ))}
        </div>

        {tab === 'deliveries' && (
          <div className="space-y-3" data-testid="comms-deliveries">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-1"><Mail className="w-4 h-4 text-blue-600" /><h2 className="font-semibold text-gray-900">Operational Email Deliveries</h2></div>
              <p className="text-xs text-gray-500">Outbound emails sent through the portal, with verified SendGrid delivery lifecycle. Open/Click evidence appears only when real provider events are received — nothing is fabricated, and a provider failure never erases the record here.</p>
              {deliveryStats.total > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="comms-delivery-stats">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Total: {deliveryStats.total}</span>
                  {Object.entries(deliveryStats.by_state).map(([s, n]) => (
                    <span key={s} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${DELIVERY_STATE_COLORS[s] || 'bg-gray-100 text-gray-700'}`}>{fmt(s)}: {n}</span>
                  ))}
                </div>
              )}
            </div>
            {deliveriesLoaded && deliveries.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-100"><EmptyState variant="no-records" title="No operational deliveries recorded yet" message="When the portal sends an email (e.g., an onboarding packet), it will appear here with its verified delivery state. No provider events have been recorded in this environment yet." testid="comms-deliveries-empty" /></div>
            )}
            {deliveries.map((d) => (
              <div key={d.message_id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`comms-delivery-${d.message_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${DELIVERY_STATE_COLORS[d.delivery_state] || 'bg-gray-100 text-gray-700'}`} data-testid={`comms-delivery-state-${d.message_id}`}>{fmt(d.delivery_state)}</span>
                      <span className="text-[11px] text-gray-500">{fmt(d.purpose)}</span>
                      {d.opened && <span className="text-[11px] text-emerald-600 flex items-center gap-1"><Eye className="w-3 h-3" />Opened</span>}
                      {d.clicked && <span className="text-[11px] text-blue-600 flex items-center gap-1"><MousePointerClick className="w-3 h-3" />Clicked</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900 mt-1 truncate">{d.subject || '(no subject)'}</h3>
                    <p className="text-sm text-gray-500">To: {d.recipient}{d.from_email ? ` · From: ${d.from_email}` : ''}</p>
                    {d.reason && <p className="text-xs text-red-600 mt-0.5">Reason: {d.reason}</p>}
                    <p className="text-[11px] text-gray-400 mt-1">{d.delivered_at ? `Delivered ${new Date(d.delivered_at).toLocaleString()}` : (d.updated_at ? `Updated ${new Date(d.updated_at).toLocaleString()}` : '')}</p>
                  </div>
                  {d.entity && (
                    <button onClick={() => navigate(d.entity.link)} data-testid={`comms-delivery-entity-${d.message_id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1.5 flex-shrink-0">
                      <ExternalLink className="w-3.5 h-3.5" /> {d.entity.name}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'communications' && (
          <>
            {/* create draft */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="comms-create">
              <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> New Communication (Draft)</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="comms-title" className="border rounded-lg px-3 py-2 text-sm" placeholder="Title"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <select data-testid="comms-type" className="border rounded-lg px-3 py-2 text-sm"
                  value={form.comm_type} onChange={(e) => setForm({ ...form, comm_type: e.target.value })}>
                  {taxonomy.comm_types.map((t) => <option key={t} value={t}>{fmt(t)}</option>)}
                </select>
              </div>
              <textarea data-testid="comms-body" className="border rounded-lg px-3 py-2 text-sm w-full mt-3" rows={3} placeholder="Body"
                value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              <div className="flex items-center justify-between mt-3">
                <label className="text-sm text-gray-600 flex items-center gap-2">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
                </label>
                <button data-testid="comms-create-btn" onClick={createComm} className="bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700">Create Draft</button>
              </div>
            </div>

            {/* list */}
            <div className="space-y-3" data-testid="comms-list">
              {items.length === 0 && <div className="bg-white rounded-xl border border-gray-100"><EmptyState variant="no-records" title="No communications yet" message="Create a draft above to start a governed company communication." testid="comms-empty" /></div>}
              {items.map((c) => (
                <div key={c.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4" data-testid={`comms-item-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-gray-400">{c.comm_number}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || 'bg-gray-100'}`} data-testid={`comms-status-${c.id}`}>{fmt(c.status)}</span>
                        <span className="text-[11px] text-gray-500">{fmt(c.comm_type)}</span>
                        {c.evidence_attached && <span className="text-[11px] text-indigo-600">• evidence: {c.evidence?.length || 0}</span>}
                      </div>
                      <h3 className="font-semibold text-gray-900 mt-1">{c.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-2">{c.body}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['draft', 'evidence_attached', 'awaiting_review'].includes(c.status) && (
                        <button data-testid={`comms-evidence-${c.id}`} onClick={() => attachEvidence(c.id)} className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700 flex items-center gap-1"><Upload className="w-3 h-3" />Evidence</button>
                      )}
                      {['draft', 'evidence_attached'].includes(c.status) && (
                        <button data-testid={`comms-submit-${c.id}`} onClick={() => act(c.id, 'submit')} className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 flex items-center gap-1"><Send className="w-3 h-3" />Submit</button>
                      )}
                      {c.status === 'awaiting_review' && isOwner && (
                        <button data-testid={`comms-approve-${c.id}`} onClick={() => act(c.id, 'approve')} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Approve</button>
                      )}
                      {['approved', 'scheduled'].includes(c.status) && isOwner && (
                        <button data-testid={`comms-publish-${c.id}`} onClick={() => act(c.id, 'publish')} className="text-xs px-2 py-1 rounded bg-blue-600 text-white flex items-center gap-1"><Megaphone className="w-3 h-3" />Publish</button>
                      )}
                      {c.status !== 'archived' && isOwner && (
                        <button data-testid={`comms-archive-${c.id}`} onClick={() => act(c.id, 'archive')} className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 flex items-center gap-1"><Archive className="w-3 h-3" />Archive</button>
                      )}
                      {['draft', 'evidence_attached'].includes(c.status) && isOwner && (
                        <button data-testid={`comms-delete-${c.id}`} onClick={() => delComm(c.id)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === 'timeline' && (
          <>
            {isOwner && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5" data-testid="timeline-create">
                <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Add Timeline Entry</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Title" data-testid="timeline-title" value={tlForm.title} onChange={(e) => setTlForm({ ...tlForm, title: e.target.value })} />
                  <input type="date" className="border rounded-lg px-3 py-2 text-sm" data-testid="timeline-date" value={tlForm.event_date} onChange={(e) => setTlForm({ ...tlForm, event_date: e.target.value })} />
                  <select className="border rounded-lg px-3 py-2 text-sm" value={tlForm.category} onChange={(e) => setTlForm({ ...tlForm, category: e.target.value })}>
                    {taxonomy.timeline_categories.map((c) => <option key={c} value={c}>{fmt(c)}</option>)}
                  </select>
                  <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Description" value={tlForm.description} onChange={(e) => setTlForm({ ...tlForm, description: e.target.value })} />
                </div>
                <button data-testid="timeline-add-btn" onClick={addTimeline} className="mt-3 bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-700">Add Entry</button>
              </div>
            )}
            <div className="space-y-3" data-testid="timeline-list">
              {timeline.length === 0 && <div className="bg-white rounded-xl border border-gray-100"><EmptyState variant="no-records" title="No timeline entries yet" message="Company milestones added by the Owner will appear here." testid="timeline-empty" /></div>}
              {timeline.map((t) => (
                <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start justify-between" data-testid={`timeline-item-${t.id}`}>
                  <div>
                    <span className="text-xs text-gray-400">{t.event_date}</span>
                    <span className="ml-2 text-[11px] font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">{fmt(t.category)}</span>
                    <h3 className="font-semibold text-gray-900 mt-1">{t.title}</h3>
                    <p className="text-sm text-gray-500">{t.description}</p>
                  </div>
                  {isOwner && <button data-testid={`timeline-delete-${t.id}`} onClick={() => delTimeline(t.id)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CommunicationsCenterPage;
